import { Fragment, useState, useCallback, ChangeEvent, MouseEvent, KeyboardEvent, ReactNode, useEffect } from 'react';
import {
    Autocomplete as MuiAutocomplete,
    AutocompleteProps as MuiAutocompleteProps,
    createFilterOptions,
    AutocompleteCloseReason,
    ClickAwayListener,
    Icon,
    IconButton,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
    styled,
} from '@mui/material';
import TextField from '../TextField';
import AutocompletePopper from './components/AutocompletePopper';
import SelectedValues from './components/SelectedValues';
import styles from './Autocomplete.module.css';
import { Check } from '@mui/icons-material';

const GroupHeader = styled('div')(({ theme }) => ({
    position: 'sticky',
    top: '-8px',
    backgroundColor: theme.palette.background.paper,
    padding: '2px 8px',
    lineHeight: '28px',
    fontSize: '0.7rem',
    fontWeight: 700,
    color: theme.palette.text.secondary,
    zIndex: theme.zIndex.modal + 200,
    display: 'flex',
    alignItems: 'center',
}));

const GroupItems = styled('ul')({
    padding: 0,
});

export interface AutocompleteProps {
    ref?: any;
    label?: string | ReactNode;
    name: string;
    value: any | null | undefined;
    selectedGroups?: any[];
    placeholder?: string;
    onChange: (value: any) => void;
    onBlur: () => void;
    autocompleteProps: Omit<
        MuiAutocompleteProps<any, boolean | undefined, boolean | undefined, boolean | undefined>,
        'renderInput'
    >;
    className?: string;
    onClear?: () => void;
    onDeleteGroup?: (id: string) => void;
    onSelectGroup?: (id: string) => void;
    selectedGroupIds?: string[];
}

const Autocomplete = ({
    ref,
    label,
    name,
    value,
    selectedGroups,
    placeholder,
    onChange,
    onBlur,
    autocompleteProps,
    className,
    onClear,
    onDeleteGroup,
    onSelectGroup,
    selectedGroupIds = [],
}: AutocompleteProps) => {
    const { multiple, options, getOptionLabel = (option) => option.label || option } = autocompleteProps;

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [pendingValue, setPendingValue] = useState<string[] | string>(() => {
        return multiple ? [] : '';
    });

    useEffect(() => {
        if (anchorEl) {
            setPendingValue(value);
        }
    }, [value, anchorEl]);

    const handleOpen = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            const defaultValue = multiple ? [] : '';
            setPendingValue(value || defaultValue);
            setAnchorEl(event.currentTarget);
        },
        [value, multiple]
    );

    const handleClear = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            const defaultValue = multiple ? [] : undefined;
            onClear?.();
            onChange(defaultValue);
        },
        [multiple]
    );

    const handleClose = useCallback(() => {
        onChange(pendingValue);
        if (anchorEl) {
            anchorEl.focus();
        }
        setAnchorEl(null);
    }, [pendingValue, onChange, anchorEl, setAnchorEl]);

    const filterOptions = createFilterOptions({
        matchFrom: 'any',
        stringify: (option) => getOptionLabel(option),
    });

    const onDelete = (value: any) => {
        setPendingValue((prevPendingValue) => {
            if (Array.isArray(prevPendingValue)) {
                const newValue = prevPendingValue.filter((item) => {
                    return autocompleteProps.getOptionKey
                        ? autocompleteProps.getOptionKey(item) !== autocompleteProps.getOptionKey(value)
                        : // @ts-ignore
                          item.id !== value.id;
                });
                onChange(newValue);
                return newValue;
            } else {
                onChange('');
                return '';
            }
        });
    };

    const handleDeleteGroup = (group: any) => {
        onDeleteGroup?.(group.id);
    };

    const showSelectedValues = () => {
        if (selectedGroups?.length) {
            return Array.isArray(value) ? !!value?.length : !!value;
        }

        return true;
    };

    return (
        <Fragment>
            <Stack spacing={1}>
                <Stack className={className} direction="row" justifyContent="space-between">
                    {typeof label === 'string' ? (
                        <Typography variant="overline" color="primary">
                            {label}
                        </Typography>
                    ) : (
                        label
                    )}
                    <Stack direction="row">
                        <IconButton size="small" onClick={handleOpen}>
                            <Icon fontSize="inherit" color="primary">
                                filter_list
                            </Icon>
                        </IconButton>
                        {value && value.length > 0 && (
                            <IconButton size="small" onClick={handleClear}>
                                <Icon fontSize="inherit" color="primary">
                                    clear
                                </Icon>
                            </IconButton>
                        )}
                    </Stack>
                </Stack>
                {selectedGroups?.length ? (
                    <SelectedValues
                        value={selectedGroups}
                        multiple={multiple}
                        placeholder={placeholder}
                        getOptionLabel={getOptionLabel}
                        handleDelete={handleDeleteGroup}
                    />
                ) : null}
                {showSelectedValues() && (
                    <SelectedValues
                        value={value}
                        multiple={multiple}
                        placeholder={placeholder}
                        getOptionLabel={getOptionLabel}
                        handleDelete={onDelete}
                    />
                )}
            </Stack>
            <AutocompletePopper open={Boolean(anchorEl)} anchorEl={anchorEl} placement="bottom-end" disablePortal>
                <ClickAwayListener onClickAway={handleClose}>
                    <div>
                        <MuiAutocomplete
                            {...autocompleteProps}
                            ref={ref}
                            onBlur={onBlur}
                            open
                            value={pendingValue}
                            onClose={(event: ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
                                if (reason === 'escape') {
                                    handleClose();
                                }
                            }}
                            onChange={(event: any, newValue: any, reason: any) => {
                                if (
                                    event.type === 'keydown' &&
                                    (event as KeyboardEvent).key === 'Backspace' &&
                                    reason === 'removeOption'
                                ) {
                                    return;
                                }
                                setPendingValue(newValue);
                            }}
                            options={
                                options
                                // multiple
                                //     ? [...options].sort((a, b) => {
                                //           let ai = pendingValue.indexOf(a);
                                //           ai = ai === -1 ? pendingValue.length + options.indexOf(a) : ai;
                                //           let bi = pendingValue.indexOf(b);
                                //           bi = bi === -1 ? pendingValue.length + options.indexOf(b) : bi;
                                //           return ai - bi;
                                //       })
                                //     : options
                            }
                            filterOptions={filterOptions}
                            renderOption={(props, option, { selected }) => (
                                <ListItem {...props} className={`${props.className} ${styles.listItem}`}>
                                    {selected && (
                                        <ListItemIcon className={styles.listItemIcon}>
                                            <Icon>done</Icon>
                                        </ListItemIcon>
                                    )}
                                    <ListItemText
                                        sx={{
                                            paddingLeft: !selected ? '32px' : 'unset',
                                        }}
                                        inset={!selected}
                                        primary={getOptionLabel(option)}
                                        primaryTypographyProps={{
                                            sx: {
                                                fontSize: '.8rem',
                                                lineHeight: 1,
                                            },
                                        }}
                                    />
                                </ListItem>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    ref={params.InputProps.ref}
                                    name={name}
                                    inputProps={params.inputProps}
                                    autoFocus
                                    sx={{
                                        width: '100%',
                                    }}
                                />
                            )}
                            renderGroup={(params) => (
                                <li key={params.key}>
                                    <GroupHeader
                                        sx={{
                                            cursor: onSelectGroup ? 'pointer' : 'default',
                                            backgroundColor: selectedGroupIds.includes(params.group)
                                                ? 'rgb(245, 245, 245)'
                                                : 'white',
                                            '&:hover': {
                                                backgroundColor: selectedGroupIds.includes(params.group)
                                                    ? 'rgb(245, 245, 245)'
                                                    : 'rgb(240, 240, 240)',
                                            },
                                        }}
                                        onClick={() => onSelectGroup && onSelectGroup(params.group)}
                                    >
                                        <Check
                                            fontSize="small"
                                            sx={{
                                                mr: 1,
                                                visibility: selectedGroupIds.includes(params.group)
                                                    ? 'visible'
                                                    : 'hidden',
                                            }}
                                        />

                                        {params.group}
                                    </GroupHeader>
                                    <GroupItems>{params.children}</GroupItems>
                                </li>
                            )}
                        />
                    </div>
                </ClickAwayListener>
            </AutocompletePopper>
        </Fragment>
    );
};

export default Autocomplete;
